import { Navigate, useLocation } from "react-router-dom";
import { useFrameworks, type FrameworkId } from "../../context/FrameworksContext";
import { LoadingBlock } from "../Spinner";
import { emptyFrame, pageContainer, type as text } from "../../theme";

/**
 * A standard's page only for a standard the member can see: enrolled by the organisation and inside
 * their role (GET /organizations/frameworks already narrows to both). Opened by URL otherwise, the page
 * would show every requirement as "Not started", fail to load what evidences each, and offer edits the
 * server refuses (lib/controlVisibility.ts). This is presentation only; the server decides access.
 */
export function StandardGate({ frameworkId, children }: { frameworkId: FrameworkId | FrameworkId[]; children: React.ReactNode }) {
  const { enrolled, loading } = useFrameworks();
  if (loading) return <LoadingBlock />;
  const ids = Array.isArray(frameworkId) ? frameworkId : [frameworkId];
  if (ids.some((id) => enrolled.has(id))) return <>{children}</>;
  return (
    <div className="page-pad" style={pageContainer}>
      <div style={{ ...emptyFrame, padding: "32px 24px" }}>
        <div style={{ ...text.body, fontWeight: 600, marginBottom: 6 }}>This standard is not available to you</div>
        <div style={text.hint}>
          Either your organisation is not enrolled in it, or it is outside the standards your role covers. Ask an administrator if you need it.
        </div>
      </div>
    </div>
  );
}

/**
 * /cyber-essentials for an organisation enrolled in Cyber Essentials Plus only: the same requirements
 * live on the Plus page with its verification parts, so links to the basic page land there.
 */
export function CyberEssentialsRoute({ children }: { children: React.ReactNode }) {
  const { enrolled, loading } = useFrameworks();
  const { search } = useLocation();
  if (loading) return <LoadingBlock />;
  if (!enrolled.has("cyber_essentials") && enrolled.has("cyber_essentials_plus")) {
    return <Navigate to={`../cyber-essentials-plus${search}`} replace />;
  }
  return <StandardGate frameworkId="cyber_essentials">{children}</StandardGate>;
}
